// Le moteur des événements : tirage maîtrisé, choix, mini-jeu.
import { EVENTS, type EventDef, type EventParams } from '../content/events';
import {
  SCAM_MAX_WRONG,
  SCAM_MIN_FOUND,
  SCAM_REWARD_FAIL,
  SCAM_REWARD_SUCCESS,
} from '../content/scam';
import { fmtEur, fmtPct } from '../engine/format';
import { t, type TextParams } from '../engine/i18n';
import { pickWeighted } from '../engine/rng';
import type { GameState } from '../engine/state';
import { DAYS_PER_YEAR, dateAtDay } from '../engine/time';
import { addJournal, addWellbeing, unlockPage } from './actions';
import { avValue } from './av';
import { peaValue } from './pea';

/**
 * L'argent du joueur : compte courant, tirelire, livrets et placements. Passer de l'un à l'autre ne change
 * rien ; seuls les frais d'un tel passage comptent (ceux d'entrée d'une assurance-vie, par exemple).
 */
function availableMoney(state: GameState): number {
  return state.cash + state.piggy + state.livret + state.ldds + state.lep + peaValue(state) + avValue(state);
}

/**
 * Un événement toutes les deux semaines de jeu : environ toutes les 35 secondes dans la vie active, et
 * toutes les 17 secondes pendant les années d'études, où le temps passe deux fois plus vite.
 */
export const EVENT_EVERY_WEEKS = { prologue: 2, adult: 2 };
/** Un événement ne revient pas avant un an de jeu, sauf s'il en décide autrement… */
export const EVENT_COOLDOWN_DAYS = 365;
/** …et, déjà vu, il pèse quatre fois moins au tirage (seize fois moins vu deux fois, etc.). */
export const SEEN_EVENT_WEIGHT = 0.25;
/**
 * Un événement de saison (Noël, soldes, rentrée…) pèse vingt fois plus pendant ses mois, tant qu'on ne l'a
 * jamais vu : sans ça, noyé parmi des centaines d'autres, il ne tomberait presque jamais au bon moment.
 */
export const SEASON_WEIGHT = 20;
/** Au moins deux mois de jeu entre deux coups durs. */
export const MIN_DAYS_BETWEEN_BAD = 60;
/** Jamais plus de deux mauvaises nouvelles d'affilée. */
export const MAX_BAD_STREAK = 2;

function weekOf(day: number): number {
  return Math.floor(day / 7);
}

/** Le mois du calendrier, de 1 (janvier) à 12 (décembre). */
function monthOf(state: GameState): number {
  return dateAtDay(state.startDate, state.day).getMonth() + 1;
}

export function getEvent(id: string): EventDef {
  const def = EVENTS.find((event) => event.id === id);
  if (!def) throw new Error(`Événement inconnu : ${id}`);
  return def;
}

/** Les paramètres d'un événement sont des montants en euros, sauf ceux qui finissent par « Pct » (des pourcentages). */
export function formatEventParams(params: EventParams): TextParams {
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, key.endsWith('Pct') ? fmtPct(value) : fmtEur(value)]),
  );
}

export function eligibleEvents(state: GameState): EventDef[] {
  return EVENTS.filter((event) => {
    if (event.manual || !event.stages.includes(state.stage)) return false;
    if (event.season && !event.season.includes(monthOf(state))) return false;
    if (event.maxCount !== undefined && (state.eventCounts[event.id] ?? 0) >= event.maxCount) return false;
    if (event.condition && !event.condition(state)) return false;
    const lastSeen = state.eventLastDays[event.id];
    if (lastSeen !== undefined && state.day - lastSeen < (event.cooldownDays ?? EVENT_COOLDOWN_DAYS)) return false;
    if (event.bad) {
      if (state.day - state.lastBadEventDay < MIN_DAYS_BETWEEN_BAD) return false;
      if (state.badStreak >= MAX_BAD_STREAK) return false;
    }
    return true;
  });
}

/**
 * Le temps d'accueil : pas de carte tirée au sort juste après l'ouverture du tableau de bord, le temps de découvrir
 * l'interface. Environ 75 secondes : deux mois pendant les études, où le temps passe deux fois plus vite, un mois sinon.
 */
export const WELCOME_DAYS = { studies: 61, work: 30 };
/** La première année d'études, une carte toutes les quatre semaines de jeu, et aucune le mois de la rentrée. */
export const FIRST_YEAR_EVENT_WEEKS = 4;

/** C'est le moment d'une nouvelle carte : assez de semaines ont passé depuis la précédente. */
export function eventDue(state: GameState): boolean {
  const studying = state.stage === 'prologue';
  const since = state.interfaceDay === null ? null : state.day - state.interfaceDay;
  if (since !== null) {
    if (since < (studying ? WELCOME_DAYS.studies : WELCOME_DAYS.work)) return false;
    // La première rentrée : la bourse et le prêt étudiant arrivent seuls.
    if (studying && since < DAYS_PER_YEAR && monthOf(state) === 9) return false;
  }
  const firstYear = studying && since !== null && since < DAYS_PER_YEAR;
  const every = firstYear ? FIRST_YEAR_EVENT_WEEKS : studying ? EVENT_EVERY_WEEKS.prologue : EVENT_EVERY_WEEKS.adult;
  return weekOf(state.day) - weekOf(state.lastEventDay) >= every;
}

/**
 * Le poids d'un événement au tirage : ce qu'on n'a jamais vu passe en priorité, sans tout regrouper d'un coup ;
 * une carte de saison passe devant le reste pendant ses mois, la première fois seulement. Une fois vue, elle
 * redevient une carte comme les autres : sinon elle reviendrait chaque année à la même saison.
 */
export function drawWeight(state: GameState, event: EventDef): number {
  const seen = state.eventCounts[event.id] ?? 0;
  const season = event.season && seen === 0 ? SEASON_WEIGHT : 1;
  return event.weight * season * SEEN_EVENT_WEIGHT ** seen;
}

/** Appelé une fois par semaine de jeu : quand c'est le moment, l'événement tiré au sort. */
export function maybeTriggerEvent(state: GameState): void {
  if (state.pendingEvent || !eventDue(state)) return;
  const candidates = eligibleEvents(state);
  if (candidates.length === 0) return;
  const picked = pickWeighted(state, candidates, (event) => drawWeight(state, event));
  startEvent(state, picked.id);
}

/** Lance la suite programmée dont le jour est venu ; renvoie vrai si une suite a commencé. */
export function startDueFollowUp(state: GameState): boolean {
  if (state.pendingEvent) return false;
  const due = state.followUps.find((followUp) => followUp.day <= state.day);
  if (!due) return false;
  state.followUps = state.followUps.filter((followUp) => followUp !== due);
  // Une suite dont la situation a changé (la voiture revendue entre-temps…) n'arrive pas.
  const def = getEvent(due.id);
  if (def.condition && !def.condition(state)) return false;
  startEvent(state, due.id, due.params);
  return true;
}

/** Commence un événement ; une suite reçoit les montants de l'événement d'origine. */
export function startEvent(state: GameState, id: string, params?: EventParams): void {
  const def = getEvent(id);
  state.pendingEvent = {
    id,
    params: { ...params, ...(def.params ? def.params(state) : {}) },
    choice: null,
    minigame: null,
    outcome: null,
  };
  state.eventCounts[id] = (state.eventCounts[id] ?? 0) + 1;
  state.eventLastDays[id] = state.day;
  state.lastEventDay = state.day;
  // Un accident touche le moral dès qu'il arrive, quel que soit le choix qui suit.
  if (def.shock && state.pendingEvent) {
    const before = state.wellbeing;
    addWellbeing(state, -def.shock);
    state.pendingEvent.shock = state.wellbeing - before;
  }
  if (def.bad) {
    state.lastBadEventDay = state.day;
    state.badStreak += 1;
  } else {
    state.badStreak = 0;
  }
  // L'événement met le temps en pause ; l'avance rapide reprend une fois la décision prise.
}

export function chooseEventOption(state: GameState, choiceId: string): void {
  const pending = state.pendingEvent;
  if (!pending || pending.choice) return;
  const def = getEvent(pending.id);
  const choice = def.choices.find((item) => item.id === choiceId);
  if (!choice || (choice.visible && !choice.visible(state, pending.params))) return;
  if (choice.available && !choice.available(state, pending.params)) return;
  const wellbeingBefore = state.wellbeing;
  const moneyBefore = availableMoney(state);
  const energyBefore = state.energy;
  const result = choice.apply(state, pending.params);
  pending.choice = choiceId;
  // Ce que le choix a réellement coûté ou rapporté, affiché au joueur.
  pending.wellbeingDelta = state.wellbeing - wellbeingBefore;
  pending.moneyDelta = availableMoney(state) - moneyBefore;
  pending.energyDelta = state.energy - energyBefore;
  addJournal(state, 'journal.event', {
    title: t(def.title),
    choice: t(choice.label, formatEventParams(pending.params)),
  });
  if (result === 'scam') {
    pending.minigame = 'scam';
    return;
  }
  pending.outcome = result;
  if (def.page) unlockPage(state, def.page);
}

export function finishScam(state: GameState, found: number, wrong: number): void {
  const pending = state.pendingEvent;
  if (!pending || pending.minigame !== 'scam') return;
  const success = found >= SCAM_MIN_FOUND && wrong <= SCAM_MAX_WRONG;
  state.knowledgePoints += success ? SCAM_REWARD_SUCCESS : SCAM_REWARD_FAIL;
  addJournal(state, 'journal.scam');
  const def = getEvent(pending.id);
  if (def.page) unlockPage(state, def.page);
  state.pendingEvent = null;
}

export function closeEvent(state: GameState): void {
  if (state.pendingEvent?.outcome) state.pendingEvent = null;
}
